<template>
  <div>
    <div
      class="group relative flex items-baseline justify-end lg:hover:bg-gray-50 lg:px-2 lg:py-1.5"
      :class="{ 'lg:bg-gray-50 lg:rounded-t-6': expanded }"
    >
      <div class="flex-1 text-gray-500 lg:text-16" :class="{ 'lg:hidden': expanded }">
        <p>
          <span>{{ $t('transfer_comparison.fee') }}</span>
          <strong v-if="fees === 0" class="uppercase text-green-800">
            {{ $t('transfer_comparison.free') }}
          </strong>
          <strong v-else class="text-gray-700">
            {{ formattedFees }}
          </strong>
        </p>
        <ExchangeRate :service="service" :mid-market-rate="midMarketRate" v-slot="{ exchangeRate }">
          <p class="mt-1">
            <span>{{ $t('exchange_rate') }}</span>
            <strong class="text-gray-700">{{ exchangeRate }}</strong>
          </p>
        </ExchangeRate>
      </div>

      <div class="text-right ml-2" :class="{ 'lg:hidden': expanded }">
        <p class="text-16 lg:text-24 font-semibold">
          {{ receivedAmount }}
        </p>
        <p
          v-if="hasPromo"
          class="text-12 lg:text-14 text-blue-900 font-semibold line-through"
          :class="{ 'lg:hidden': expanded }"
        >
          {{ standardAmount }}
        </p>
        <p
          v-if="service.quote.fee.externalPayin || service.quote.fee.externalPayout"
          class="text-12 text-amber-800 font-semibold"
          :class="{ 'lg:hidden': expanded }"
        >
          {{ $t('label.estimated') }}
        </p>
      </div>

      <span
        class="group-hover:bg-gray-200 hidden lg:inline-block p-1.5 rounded-full self-start mt-1 ml-2"
      >
        <svg role="presentation" focusable="false" tabindex="-1" :class="['flex icon icon-xs', { 'rotate-180': expanded }]"
           
          alt="expand"
          svg-inline
         xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 32 19"><title>expand</title>
  <path fill="currentColor" fill-rule="evenodd"
    d="M.8 1.18a2.26 2.26 0 0 1 3.2 0L16 13.2 28.03 1.18a2.26 2.26 0 0 1 3.2 3.2L17.6 17.97c-.88.9-2.31.9-3.2 0L.8 4.39a2.26 2.26 0 0 1 0-3.2Z"
    clip-rule="evenodd" />
</svg>
      </span>

      <button
        class="hidden lg:block absolute w-full inset-0"
        :title="$t('details')"
        :aria-label="$t('details')"
        @click="toggleExpanded"
      />
    </div>
    <ServiceDetails
      v-show="expanded"
      class="hidden lg:block bg-gray-50 text-16 rounded-b-6 p-4 pt-2"
      :service="service"
      :provider="provider"
      :mid-market-rate="midMarketRate"
    />

    <ProviderPromotion
      v-if="!hasCommonPromo && service.promotion"
      class="hidden lg:flex mt-2"
      :provider-display-name="provider.displayName"
      :provider-logo="provider.logo.sm"
      :promotion="service.promotion"
      @open="sendPromoAnalytics"
    >
      <template #link>
        <ProviderLink
          v-if="isRecommended"
          class="mx-auto mb-4"
          :provider="provider"
          :promotion="service.promotion"
          location="promotion_modal"
        />
      </template>
    </ProviderPromotion>
  </div>
</template>

<script lang="ts" setup>
import type { Corridor } from '@core/types/imt'
import type { Service, Provider } from './types'
import { panelOpen } from './analytics/actions'
import ProviderLink from './provider-link.vue'
import ServiceDetails from './service-details.vue'
import ExchangeRate from './exchange-rate.vue'
import { Comparison } from './analytics/objects/Comparison'
import { ComparisonSection } from './analytics/objects/ComparisonSection'
import { ComparisonResult } from './analytics/objects/ComparisonResult'
import { isRecommendedProvider } from './helpers/recommended'
import { totalFees } from './helpers/pricing'

const props = defineProps<{
  provider: Provider
  service: Service
  hasCommonPromo?: boolean
  midMarketRate: number
}>()

const { $track, $t, $lang } = useNuxtApp()

const corridor = inject('corridor') as Corridor
const getResultAnalyticsObj = inject('getResultAnalyticsObj') as (
  slug: string,
  location?: string,
) => ComparisonResult | undefined
const sectionAnalyticsObj = inject<Ref<ComparisonSection>>('sectionAnalyticsObj')
const comparisonAnalyticsObj = inject<Ref<Comparison>>('comparisonAnalyticsObj')

const expanded = ref(false)

async function toggleExpanded() {
  expanded.value = !expanded.value
  if (expanded.value) {
    await panelOpen(
      $track,
      'Cost Breakdown',
      comparisonAnalyticsObj?.value,
      sectionAnalyticsObj?.value,
      getResultAnalyticsObj(props.provider.slug),
    )
  }
}

async function sendPromoAnalytics() {
  await panelOpen(
    $track,
    'Promotion Details',
    comparisonAnalyticsObj?.value,
    sectionAnalyticsObj?.value,
    getResultAnalyticsObj(props.provider.slug),
  )
}

const receivedAmount = computed(() => {
  return formatMoney(props.service.amount, corridor.currencyTo.iso, $lang)
})

const isRecommended = computed(() => {
  return isRecommendedProvider(props.provider)
})

const standardAmount = computed(() => {
  return formatMoney(props.service.quote.receivedAmount, corridor.currencyTo.iso, $lang)
})

const hasPromo = computed(() => {
  return props.service.promotion && receivedAmount.value !== standardAmount.value
})

const fees = computed(() => {
  return totalFees(props.service.quote)
})

const formattedFees = computed(() => {
  return formatMoney(fees.value, corridor.currencyFrom.iso, $lang, 2)
})
</script>
